import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Eye, Pencil, ArrowLeft, Save, Send, Sparkles, BookOpenCheck, Wand2, PanelLeftClose, PanelLeftOpen, Download } from 'lucide-react'
import { useLessonBuilder } from '../store/useLessonBuilder'
import { lessonApi, wordToLessonItems } from '../api/lessonBuilderApi'
import { normalizeBlocks, lessonCtx } from '../types/blocks'
import DraggablePalette from '../components/DraggablePalette'
import SectionCanvas from '../components/SectionCanvas'
import LessonMetaForm from '../components/LessonMetaForm'
import BuilderPreview from '../components/BuilderPreview'
import LessonCardRenderer from '../components/LessonCardRenderer'
import FileDropzone from '../components/FileDropzone'
import AiLessonWizard from '../components/AiLessonWizard'
import ResourcePanel from '../components/ResourcePanel'
import { buildSampleBai1 } from '../lib/sampleLesson'
import { genId } from '../lib/id'
import { GeoGebraService } from '../../../services/video/GeoGebraService'
import type { CardType, SimulationCard } from '../types/LessonDoc'

const DOCX_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
}

const LessonBuilderPage: React.FC = () => {
  const { lesson, selectedSectionId, addCard, addRichCard, addPreparedCard, setMeta, newLesson, loadLesson } = useLessonBuilder()
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [aiBusy, setAiBusy] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [previewSec, setPreviewSec] = useState<string>('all')
  const [wizardOpen, setWizardOpen] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  // Co giãn 3 cột (chỉ desktop): w = [%trái, %giữa]; phải = phần còn lại
  const splitRef = useRef<HTMLDivElement>(null)
  const [w, setW] = useState<[number, number]>([25, 42])
  const [asideOpen, setAsideOpen] = useState(true)   // ẩn cột trái để soạn rộng hơn
  const [isDesktop, setIsDesktop] = useState(false)
  const dragRef = useRef<{ i: 0 | 1; startX: number; base: [number, number] } | null>(null)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const f = () => setIsDesktop(mq.matches); f()
    mq.addEventListener('change', f); return () => mq.removeEventListener('change', f)
  }, [])
  const startDrag = (i: 0 | 1) => (e: React.PointerEvent) => {
    dragRef.current = { i, startX: e.clientX, base: [...w] as [number, number] }
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId) } catch { /* noop */ }
  }
  const onDrag = (e: React.PointerEvent) => {
    const d = dragRef.current, box = splitRef.current; if (!d || !box) return
    const pct = ((e.clientX - d.startX) / box.getBoundingClientRect().width) * 100
    let [a, b] = d.base
    if (d.i === 0) { a = Math.max(15, Math.min(50, a + pct)); if (100 - a - b < 18) b = 100 - a - 18 }
    else { b = Math.max(20, Math.min(60, b + pct)); if (100 - a - b < 18) b = 100 - a - 18 }
    setW([a, b])
  }
  const endDrag = (e: React.PointerEvent) => { dragRef.current = null; try { (e.currentTarget as Element).releasePointerCapture(e.pointerId) } catch { /* noop */ } }
  const dividerProps = (i: 0 | 1) => ({
    onPointerDown: startDrag(i), onPointerMove: onDrag, onPointerUp: endDrag, onPointerCancel: endDrag,
    className: 'self-stretch w-3 shrink-0 cursor-col-resize flex items-center justify-center group touch-none',
  })
  // Khi ẩn cột trái: canvas + xem trước chia nhau 100% theo đúng tỉ lệ cũ
  const restW = 100 - w[0] - w[1]
  const canvasW = asideOpen ? w[1] : (w[1] / (w[1] + restW)) * 100
  const previewW = asideOpen ? restW : (restW / (w[1] + restW)) * 100
  const { id } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    if (id) {
      lessonApi.get(id).then((doc) => {
        if (doc) loadLesson(doc)
      })
    } else {
      newLesson()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleSave = async (publish: boolean) => {
    if (!lesson.meta.tieuDe.trim()) {
      alert('Vui lòng nhập tiêu đề bài học trước khi lưu.')
      return
    }
    const saved = await lessonApi.save(lesson)
    loadLesson(saved)
    setSavedAt(new Date().toLocaleTimeString('vi-VN'))
    if (publish) {
      const pub = await lessonApi.publish(saved.id)
      navigate(`/bai-giang/${(pub || saved).slug}`)
    } else if (!id) {
      navigate(`/soan-bai/${saved.id}`, { replace: true })
    }
  }

  // Kéo-thả .docx → MỞ wizard (đề xuất khung → GV sửa → mới sinh chi tiết). KHÔNG tự sinh ngay.
  const handleWordImport = (file: File) => {
    setAiError(null)
    setPendingFile(file)
    setWizardOpen(true)
  }

  // Bấm chip ở kho thẻ → thêm vào phần đang chọn (mặc định phần đầu)
  const handlePaletteAdd = (type: CardType) => {
    const target = lesson.sections.find((s) => s.id === selectedSectionId) || lesson.sections[0]
    if (target) addCard(target.id, type)
  }

  const previewSection = lesson.sections.find((s) => s.id === previewSec)

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Thanh tiêu đề */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/teacher/elearning')} className="text-gray-400 hover:text-gray-700">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-black text-gray-900">Soạn bài giảng</h1>
          <button
            onClick={() => {
              if (lesson.sections.some((s) => s.cards.length) && !confirm('Tải bài mẫu sẽ thay nội dung hiện tại?')) return
              loadLesson(buildSampleBai1())
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100"
          >
            <BookOpenCheck size={14} /> Tạo bài mẫu
          </button>
          {savedAt && <span className="text-xs text-gray-400">Đã lưu lúc {savedAt}</span>}
          {mode === 'edit' && (
            <button
              onClick={() => setAsideOpen((v) => !v)}
              title={asideOpen ? 'Ẩn cột trái (soạn rộng hơn)' : 'Hiện cột trái'}
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200"
            >
              {asideOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleSave(false)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200">
            <Save size={16} /> Lưu nháp
          </button>
          <button onClick={() => handleSave(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700">
            <Send size={16} /> Xuất bản
          </button>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button onClick={() => setMode('edit')} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${mode === 'edit' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>
              <Pencil size={15} /> Soạn
            </button>
            <button onClick={() => setMode('preview')} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${mode === 'preview' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>
              <Eye size={15} /> Toàn màn hình
            </button>
          </div>
        </div>
      </div>

      {mode === 'preview' ? (
        <div className="max-w-3xl mx-auto p-6">
          <BuilderPreview lesson={lesson} />
        </div>
      ) : (
        <div ref={splitRef} className="flex flex-col lg:flex-row gap-4 lg:gap-0 p-4 items-start">
          {/* Trái: thông tin + Word-AI (ẩn được để soạn rộng hơn) */}
          {asideOpen && (
          <aside className="w-full min-w-0 space-y-4" style={isDesktop ? { width: `${w[0]}%` } : undefined}>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Thông tin bài</h3>
              <LessonMetaForm meta={lesson.meta} onChange={setMeta} />
            </div>
            <div className="bg-white rounded-xl border-2 border-violet-200 p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-black text-violet-700 uppercase tracking-wider mb-2">
                <Sparkles size={14} /> Tạo bài học từ Word (AI)
              </h3>
              <FileDropzone
                accept={DOCX_ACCEPT}
                onFile={handleWordImport}
                busy={aiBusy}
                label="Kéo-thả .docx → AI đề xuất khung để bạn sửa"
                hint="Xem & chỉnh khung + nội dung TRƯỚC khi sinh (không tự sinh ngay). pmaker đọc công thức & ảnh."
              />
              {/* Nút tải file Word mẫu — to, nổi bật, NGAY DƯỚI khung kéo-thả */}
              <a href="/mau-khbd-5512.docx" download
                className="flex items-center justify-center gap-2.5 w-full mt-3 px-5 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-base font-extrabold shadow-md shadow-amber-500/30 transition-all hover:shadow-lg hover:-translate-y-0.5">
                <Download className="w-5 h-5" /> Tải file Word giáo án mẫu (KHBD 5512)
              </a>
              <p className="text-center text-xs text-gray-400 mt-1.5">Chưa có file? Tải mẫu trên về, soạn theo form I·II·III·IV rồi kéo-thả lên.</p>
              {aiError && <p className="text-xs text-red-600 mt-2">{aiError}</p>}
              <AiLessonWizard isOpen={wizardOpen} initialFile={pendingFile} onClose={() => { setWizardOpen(false); setPendingFile(null) }} />
            </div>
            <div className="bg-white rounded-xl border-2 border-emerald-200 p-4">
              <ResourcePanel />
            </div>
          </aside>
          )}

          {isDesktop && asideOpen && <div {...dividerProps(0)}><div className="w-1 h-20 rounded-full bg-gray-200 group-hover:bg-blue-400 transition-colors" /></div>}

          {/* Giữa: canvas các phần */}
          <section className="w-full min-w-0" style={isDesktop ? { width: `${canvasW}%` } : undefined}>
            <SectionCanvas />
          </section>

          {isDesktop && <div {...dividerProps(1)}><div className="w-1 h-20 rounded-full bg-gray-200 group-hover:bg-blue-400 transition-colors" /></div>}

          {/* Phải: xem trước (từng phần / toàn bài) */}
          <section className="w-full min-w-0" style={isDesktop ? { width: `${previewW}%` } : undefined}>
            <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Xem trước</h3>
                <select
                  value={previewSec}
                  onChange={(e) => setPreviewSec(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none bg-white"
                >
                  <option value="all">Toàn bài</option>
                  {lesson.sections.map((s, i) => (
                    <option key={s.id} value={s.id}>
                      Phần {i + 1}: {s.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
                {previewSec === 'all' || !previewSection ? (
                  <BuilderPreview lesson={lesson} />
                ) : (
                  <div className="space-y-4">
                    <h2 className="text-lg font-black text-blue-900">{previewSection.title}</h2>
                    {previewSection.cards.length === 0 ? (
                      <p className="text-sm text-gray-400">Phần này chưa có thẻ.</p>
                    ) : (
                      previewSection.cards.map((card) => (
                        <div key={card.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                          <LessonCardRenderer card={card} />
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default LessonBuilderPage
